import { AUTH } from '@hotjar/webapp/constants/routes';
import { getLoggedIn } from '@hotjar/webapp/domains/common/user-data';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  isDemoSiteURL,
  redirectToDemoLogin,
} from '@hotjar/webapp/domains/acquisition/demo/demo-site-redirections';
import { measureAndLog } from '@hotjar/performance';
import { MARKS } from '@hotjar/webapp/performance';

interface AuthenticatedRouteProps {
  children: JSX.Element;
}

/**
 * A component that checks a user is authenticated before rendering the route.
 *
 * <Route path="/<some_path>" element={
 *   <AuthenticatedRoute>
 *     <MyComponent />
 *   </AuthenticatedRoute>
 * } />
 */
export const AuthenticatedRoute = ({ children }: AuthenticatedRouteProps): JSX.Element | null => {
  // It is sufficient to check authenticated state only when trying to render
  // the route since we will handle logging out or 401 events separately.
  const isLoggedIn = getLoggedIn();
  const location = useLocation();

  useEffect(() => {
    if (!isLoggedIn) {
      const loginScreenUrl = new URL(AUTH.LOGIN.path, window.location.href);
      loginScreenUrl.searchParams.set('next', location.pathname);

      if (isDemoSiteURL(location.pathname, location.search)) {
        redirectToDemoLogin();
        return;
      }

      // If the users isn't logged in, we redirect to the login page.
      window.location.href = loginScreenUrl.toString();
    }
  }, [isLoggedIn, location]);

  useEffect(() => {
    // Perf: Mark authenticated route wrapper render timings
    measureAndLog({
      name: MARKS.APP_ROUTE_AUTH,
      startMark: MARKS.APP_RENDERED,
      addMark: true,
    });
  }, []);

  if (isLoggedIn) {
    return children;
  }

  return null;
};
